import React, { useState, useEffect } from 'react';
import {
  UserPen, Plus, Edit3, Trash2, Mail, Briefcase, FileText,
  AlertCircle, Check, X, User
} from 'lucide-react';
import { adminApi } from '../../lib/adminApi';
import { Button } from '../../components/ui/button';
import BlogNavTabs from './BlogNavTabs';
import './AdminBlog.css';

export default function AdminBlogAuthors() {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete State
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAuthors = () => {
    setLoading(true);
    setError('');
    adminApi.get('/admin/blog-authors')
      .then(res => setAuthors(res.data || []))
      .catch(err => setError(err.response?.data?.detail || "Failed to load authors."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAuthors();
  }, []);

  const openCreateModal = () => {
    setEditingAuthor(null);
    setName('');
    setTitle('');
    setBio('');
    setAvatarUrl('');
    setEmail('');
    setModalOpen(true);
  };

  const openEditModal = (author) => {
    setEditingAuthor(author);
    setName(author.name || '');
    setTitle(author.title || '');
    setBio(author.bio || '');
    setAvatarUrl(author.avatar_url || '');
    setEmail(author.email || '');
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    setError('');
    const payload = {
      name: name.trim(),
      title: title.trim(),
      bio: bio.trim(),
      avatar_url: avatarUrl.trim(),
      email: email.trim()
    };

    try {
      if (editingAuthor) {
        await adminApi.put(`/admin/blog-authors/${editingAuthor.id}`, payload);
        setSuccess(`Author "${name}" updated successfully.`);
      } else {
        await adminApi.post('/admin/blog-authors', payload);
        setSuccess(`Author "${name}" created successfully.`);
      }
      setModalOpen(false);
      fetchAuthors();
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save author.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (authorId) => {
    setDeleting(true);
    try {
      await adminApi.delete(`/admin/blog-authors/${authorId}`);
      setSuccess("Author removed.");
      setDeleteConfirmId(null);
      fetchAuthors();
    } catch (err) {
      setError("Failed to delete author.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <main className="space-y-6">
      <BlogNavTabs
        title="Authors"
        description="Manage writer profiles, editorial contributors, and author bios."
        showCreateButton={false}
      />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 border border-slate-200 rounded-xl shadow-sm">
        <div>
          <h3 className="text-base font-bold text-slate-900">Blog Authors & Contributors</h3>
          <p className="text-xs text-slate-500">
            Assigned authors will appear on the blog articles and contribute to author SEO schema.
          </p>
        </div>
        <Button
          onClick={openCreateModal}
          className="admin-primary flex items-center gap-2 text-xs font-semibold"
        >
          <Plus size={15} />
          <span>Add New Author</span>
        </Button>
      </div>

      {/* Notifications */}
      {error && (
        <div role="alert" className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div role="status" className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg flex items-center gap-2 text-sm">
          <Check size={16} />
          <span>{success}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white border rounded-xl p-8 text-center text-slate-500">
          <div className="admin-spinner mb-3" />
          <p className="text-sm">Loading authors...</p>
        </div>
      )}

      {/* Authors List */}
      {!loading && authors.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-slate-500 space-y-3">
          <User size={36} className="mx-auto text-slate-300" />
          <h4 className="text-base font-semibold text-slate-800">No authors added yet</h4>
          <p className="text-xs text-slate-500">Click &ldquo;Add New Author&rdquo; above to create your first contributor profile.</p>
        </div>
      )}

      {!loading && authors.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {authors.map((author) => (
            <div
              key={author.id}
              className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:border-slate-300 transition-all flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  {author.avatar_url ? (
                    <img
                      src={author.avatar_url}
                      alt={author.name}
                      className="w-12 h-12 rounded-full object-cover border border-slate-200"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-900 font-bold flex items-center justify-center text-base border border-amber-200">
                      {(author.name || 'A')[0].toUpperCase()}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-bold text-slate-900 truncate">{author.name}</h4>
                    {author.title && (
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <Briefcase size={12} className="text-slate-400" />
                        <span className="truncate">{author.title}</span>
                      </p>
                    )}
                    {author.email && (
                      <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5 truncate">
                        <Mail size={12} className="text-slate-400" />
                        <span className="truncate">{author.email}</span>
                      </p>
                    )}
                  </div>
                </div>

                {author.bio && (
                  <p className="text-xs text-slate-600 line-clamp-3 leading-relaxed bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                    {author.bio}
                  </p>
                )}
              </div>

              <div className="pt-4 mt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1 font-medium text-slate-700">
                  <FileText size={13} className="text-amber-600" />
                  <span>{author.post_count || 0} posts</span>
                </span>

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(author)}
                    className="h-7 px-2 text-xs text-slate-600 hover:text-amber-600 hover:bg-amber-50"
                  >
                    <Edit3 size={13} className="mr-1" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteConfirmId(author.id)}
                    className="h-7 px-2 text-xs text-slate-400 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal: Add / Edit Author ── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={handleSave} className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b pb-3">
              <h4 className="font-bold text-slate-900 text-base">
                {editingAuthor ? 'Edit Author' : 'Add New Author'}
              </h4>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Author Name *</label>
                <input
                  type="text"
                  required
                  placeholder="Enter author's full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Role / Job Title</label>
                <input
                  type="text"
                  placeholder="e.g. Senior SEO Consultant"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Email (Optional)</label>
                <input
                  type="email"
                  placeholder="author@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Avatar Image URL (Optional)</label>
                <input
                  type="url"
                  placeholder="https://example.com/avatar.jpg"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Bio / About</label>
                <textarea
                  rows={3}
                  placeholder="Brief background and expertise of the author..."
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="w-full text-sm border rounded-lg p-2.5 focus:border-amber-500 focus:outline-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={saving || !name.trim()}
                className="admin-primary"
              >
                {saving ? 'Saving...' : (editingAuthor ? 'Save Changes' : 'Create Author')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* ── Modal: Delete Confirmation ── */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4">
            <h4 className="font-bold text-slate-900 text-base">Delete author profile?</h4>
            <p className="text-xs text-slate-600 leading-relaxed">
              Are you sure? Existing articles written by this author will keep their content but be unlinked.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteConfirmId(null)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={deleting}
                onClick={() => handleDelete(deleteConfirmId)}
                className="admin-danger"
              >
                {deleting ? 'Deleting...' : 'Delete Author'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
